/**
 * InAppNotificationPopup Component
 * Displays in-app notification popups with auto-dismiss
 */

import React, { useEffect } from 'react';
import { useNotificationStore } from '@/stores/notification';
import { useNavigate } from 'react-router-dom';
import { X } from '@phosphor-icons/react';
import Button from '@/components/common/Button';

const InAppNotificationPopup: React.FC = () => {
  const navigate = useNavigate();
  const { activePopups, removeFromPopupQueue, markAsRead } = useNotificationStore();

  // Auto-dismiss notifications
  useEffect(() => {
    activePopups.forEach((notification) => {
      const dismissTime = notification.type === 'intervention_escalated' ? 15000 : 10000;
      
      const timer = setTimeout(() => {
        removeFromPopupQueue(notification.id);
      }, dismissTime);

      return () => clearTimeout(timer);
    });
  }, [activePopups, removeFromPopupQueue]);

  const handleViewDetails = (notificationId: string) => {
    markAsRead(notificationId);
    removeFromPopupQueue(notificationId);
    navigate('/interventions');
  };

  const handleDismiss = (notificationId: string) => {
    removeFromPopupQueue(notificationId);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
        return 'bg-red-500';
      case 'HIGH':
        return 'bg-orange-500';
      case 'MEDIUM':
        return 'bg-yellow-500';
      case 'LOW':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'intervention_escalated':
        return '🚨';
      case 'intervention_assigned':
        return '📋';
      case 'intervention_required':
        return '⚠️';
      default:
        return '📢';
    }
  };

  if (activePopups.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-20 right-6 z-50 space-y-3 max-w-md">
      {activePopups.map((notification, index) => (
        <div
          key={notification.id}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden animate-slide-in-right"
          style={{
            animationDelay: `${index * 100}ms`,
          }}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-4 pb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{getTypeIcon(notification.type)}</span>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100">{notification.title}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full text-white font-medium ${getPriorityColor(
                      notification.priority
                    )}`}
                  >
                    {notification.priority}
                  </span>
                  {notification.interventionType && (
                    <span className="text-xs text-gray-500">
                      {notification.interventionType.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => handleDismiss(notification.id)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              aria-label="Dismiss notification"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 pb-2">
            <p className="text-sm text-gray-700 dark:text-gray-200">{notification.message}</p>
            {notification.requestingAgent && (
              <p className="text-xs text-gray-500 mt-1">
                Requested by: {notification.requestingAgent}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 p-4 pt-2 bg-gray-50 dark:bg-gray-900">
            <Button
              size="sm"
              variant="primary"
              onClick={() => handleViewDetails(notification.id)}
              className="flex-1"
            >
              View Details
            </Button>
            {notification.quickApprove && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  // TODO: Implement quick approve
                  handleDismiss(notification.id);
                }}
                className="flex-1"
              >
                Quick Approve
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleDismiss(notification.id)}
            >
              Dismiss
            </Button>
          </div>

          {/* Progress bar for auto-dismiss */}
          <div className="h-1 bg-gray-200">
            <div
              className="h-full bg-blue-500 animate-progress"
              style={{
                animationDuration:
                  notification.type === 'intervention_escalated' ? '15s' : '10s',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default InAppNotificationPopup;
